import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { COLORS, FONT_BODY, NAV_CLEARANCE } from "../theme.js";

const ToastContext = createContext(() => {});

/** Call `showToast("Flight logged")` from anywhere inside the app shell. */
// eslint-disable-next-line react-refresh/only-export-components
export function useToast() {
  return useContext(ToastContext);
}

export function Toast({ message }) {
  if (!message) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "absolute",
        /* Clear of the bottom nav, which is ~73px of padding and content plus
           whatever the device reserves for its home indicator. The old flat 90
           ignored that inset, so on any iPhone that has one the toast rendered
           behind the nav — visible only on hardware that doesn't reserve it,
           which is why it survived every desktop check. */
        bottom: `calc(${NAV_CLEARANCE} + 15px)`,
        left: "50%",
        transform: "translateX(-50%)",
        background: COLORS.blackCard2,
        border: `1px solid ${COLORS.gold}`,
        borderRadius: 10,
        padding: "10px 18px",
        fontFamily: FONT_BODY,
        fontSize: 13,
        color: COLORS.cream,
        boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
        zIndex: 100,
        whiteSpace: "nowrap",
        animation: "toastIn 0.2s ease-out",
      }}
    >
      <style>{"@keyframes toastIn { from { opacity: 0; transform: translateX(-50%) translateY(8px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }"}</style>
      ✓ {message}
    </div>
  );
}

export function ToastProvider({ children }) {
  const [message, setMessage] = useState(null);
  const timer = useRef(null);

  const showToast = useCallback((text) => {
    setMessage(text);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setMessage(null), 2200);
  }, []);

  // A toast outliving the component that triggered it would set state on an
  // unmounted tree, so clear the pending timer on the way out.
  useEffect(() => () => clearTimeout(timer.current), []);

  return (
    <ToastContext.Provider value={showToast}>
      {children}
      <Toast message={message} />
    </ToastContext.Provider>
  );
}
